import { addDays, format } from "date-fns";
import { useState, useRef, useEffect } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import CalendarIcon from "../../icons/calendar.icon";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import { OrderListFilter } from "../../types/order";

export default function SortByDate({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { msg, t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>();
  const selectedRange = useWatch({ control: methods.control, name: "date" });

  useEffect(() => {
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  function onClick(e: MouseEvent) {
    // This is a hack to get the latest state
    if (!ref || !ref.current) return;
    const isOpened = (ref.current as any).getAttribute("data-state");

    if (!isOpened) return;

    const isWithin = (e.target as any).closest("#sortbydate-selector");

    if (!isWithin) {
      setIsOpened(false);
    }
  }

  const DATES = [
    { from: addDays(new Date(), 0), name: t(msg.date.today), to: new Date() },
    { from: addDays(new Date(), -7), name: t(msg.date.last7Days), to: new Date() },
    { from: addDays(new Date(), -30), name: t(msg.date.last30Days), to: new Date() },
    { from: addDays(new Date(), -90), name: t(msg.date.lastQuater), to: new Date() },
    { from: addDays(new Date(), -365), name: t(msg.date.last365Days), to: new Date() },
  ];

  function toggleDatePanel() {
    setIsOpened(!isOpened);
  }

  function setDate(from: Date, to: Date) {
    methods.setValue("date", {
      from: format(from, "yyyy-MM-dd"),
      to: format(to, "yyyy-MM-dd"),
    });
  }

  const currentDate = format(new Date(), "yyyy-MM-dd");

  return (
    //  @ts-ignore
    <div className="relative" ref={ref} id="sortbydate-selector" data-state={isOpened}>
      <button
        onClick={toggleDatePanel}
        className="rounded-md text-sm px-4 py-2 bg-white dark:bg-her-darkblue2 dark:text-white flex items-center gap-3"
      >
        <CalendarIcon className="h-5 w-5 text-her-pumpkin" />
        <DateName methods={methods} />
        <ChevronDownIcon className="h-5 w-5" />
      </button>

      {isOpened && (
        <section className="flex flex-col absolute text-sm z-50 bg-white dark:bg-pos-dark dark:text-white text-black top-11 w-full shadow-lg">
          {DATES.map((d) => (
            <button
              key={d.name}
              type="button"
              className="border-b py-2 hover:bg-blue-200 hover:text-blue-600"
              onClick={() => {
                setDate(d.from, d.to);
                setIsOpened(false);
              }}
            >
              {d.name}
            </button>
          ))}

          <button type="button" className="border-b py-2 hover:bg-blue-200 hover:text-blue-600">
            {t(msg.date.customDate)}
          </button>

          <section className="bg-white dark:bg-pos-dark shadow-md p-5 z-50 space-y-4">
            <div>
              <label className="block">{t(msg.formLabels.selectDateFrom)}</label>
              <input
                type="date"
                max={selectedRange?.to ?? currentDate}
                {...methods.register("date.from")}
                placeholder={t(msg.formLabels.selectDateFrom)}
                className="py-2 px-4 text-gray-700 border"
              />
            </div>

            <div>
              <label className="block">{t(msg.formLabels.selectDateTo)}</label>
              <input
                type="date"
                min={selectedRange?.from ?? ""}
                max={currentDate}
                {...methods.register("date.to")}
                placeholder={t(msg.formLabels.selectDateFrom)}
                className="py-2 px-4 text-gray-700 border"
              />
            </div>
          </section>
        </section>
      )}
    </div>
  );
}

function DateName({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const [date] = useWatch({ control: methods.control, name: ["date"] });

  return (
    <span>
      {date.from} - {date.to}
    </span>
  );
}
